/**
 * Haldi decorations — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_decor_ideas`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const haldiDecorationsPack: ToilePack = {
    slug: 'haldi-decorations',
    category: 'occasions',
    name: 'Haldi decorations',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'marigold', label: 'Marigold backdrop', subject: 'a wall of hanging yellow marigold strings with a low wooden seat in front'},
        {key: 'floor', label: 'Floor seating', subject: 'floor cushions and brass urlis with petals'},
        {key: 'swing', label: 'Swing', subject: 'a flower-wrapped swing for the bride'},
    ],
    styles: [
        {key: 'courtyard', label: 'Courtyard', style: 'set in a paved home courtyard with potted tulsi and a carved wooden door behind, an eye-level wide view', palette: 'marigold yellow and orange against whitewashed walls', framing: 'the setup centered, the floor in front'},
        {key: 'terrace', label: 'Terrace', style: 'set on a rooftop terrace under a yellow cotton canopy, neighbouring rooftops soft behind, a slightly high view', palette: 'yellow and leaf green with pale sky behind', framing: 'the setup in the lower two thirds'},
        {key: 'livingroom', label: 'Living room', style: 'set in a small living room with the sofa pushed aside, a close three-quarter view at seat height', palette: 'yellow and white on a warm wooden floor', framing: 'the setup filling the frame'},
    ],
    directions: ['mango leaves', 'pink and yellow', 'mehndi night'],
    directionHint: 'try: mango leaves',
    seo: {
        title: 'Haldi decoration: marigold backdrop, seating, swing',
        metaDescription: 'Nine haldi decoration pictures for a home courtyard, rooftop or living room: a marigold backdrop, floor seating and a flower swing.',
        h1: 'Haldi decoration: marigold backdrop, floor seating, swing',
        tagline: 'Nine pictures, three haldi pieces, each set in a courtyard, on a rooftop and in a living room.',
        intro: 'Haldi decoration for a family ceremony at home: a wall of hanging yellow marigold strings with a low wooden seat, floor cushions with brass urlis and petals, and a swing wrapped in flowers for the bride. Each piece is set in a paved courtyard, on a rooftop under a yellow canopy, or in a small living room. Type a twist, such as mango leaves or pink and yellow, and the same setups carry it.',
        useCases: [
            'a haldi ceremony in a home courtyard',
            'a rooftop haldi with family',
            'a living room haldi for a small group',
            'a reference for a florist or decorator',
        ],
        faq: [
            {q: 'What do the three pieces show?', a: 'A marigold backdrop with a low wooden seat, floor seating with brass urlis and petals, and a swing wrapped in flowers.'},
            {q: 'Where are the nine pictures set?', a: 'Each piece appears in three places: a paved home courtyard with potted tulsi, a rooftop under a yellow cotton canopy, and a small living room.'},
            {q: 'Can I change the colours?', a: 'Type a direction such as pink and yellow or mango leaves, and the same setups are shown with it.'},
            {q: 'Are these real photographs?', a: 'No. They are generated pictures made to look like photographs. There are no people in them.'},
        ],
    },
}
